import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import {
  EVENTS,
  MSDFTextGeometry,
  MSDFTextNodeMaterial,
  oklchToHex,
  rgbToHex,
  scaleUV,
  scaleValue,
} from "@folio-2026/shared"
import { TextScene } from "@folio-2026/text"
import gsap from "gsap"
import { InteractionManager } from "three.interactive"
import {
  abs,
  attribute,
  clamp,
  color,
  float,
  Fn,
  fract,
  mix,
  mul,
  pass,
  pow,
  sin,
  smoothstep,
  step,
  time,
  uniform,
  uv,
  vec2,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { Texture } from "three/webgpu"
import { FolderApi } from "tweakpane"

import Image from "./Image"
import ProjectLeaf from "./ProjectLeaf"

type TextMeshData = {
  mesh: THREE.Mesh
  material: MSDFTextNodeMaterial
  geometry: MSDFTextGeometry
  element: HTMLElement
  bounds: DOMRect
  y: number
}

export default class ProjectList {
  experience: Experience
  sizes: Experience["sizes"]
  renderer: Experience["renderer"]["instance"]
  debugFolder?: FolderApi
  postProcessing!: THREE.PostProcessing
  scenePassTextureNode!: THREE.TextureNode
  scene!: THREE.Scene
  resources: {
    [key: string]: Texture
  }
  distanceFromCamera = 0.2
  camera!: THREE.PerspectiveCamera
  textScene: TextScene
  texts: TextScene["texts"] = []
  projectImage!: Image
  cardBounds: DOMRect | null = null
  containerBounds: DOMRect | null = null
  projectsElement: HTMLElement | null = null
  cardsElements: HTMLElement[] = []
  cards: THREE.Group[] = []
  cardMaterials: THREE.MeshBasicNodeMaterial[] = []
  cardMaterialSettings = {
    opacity: 0.7,
    color: "#161616",
  }
  // VHS effect settings
  vhsSettings = {
    scanlineCount: uniform(149),
    flickerIntensity: uniform(0.12),
  }
  // Border settings (left and right sides only)
  borderSettings = {
    thickness: uniform(0.002),
    color: "#196a85",
  }
  cardY: number = 0
  textMeshes: TextMeshData[][] = [] // Array of text meshes per card
  projectLeaf: ProjectLeaf | null = null
  screenHeightUniform: THREE.UniformNode<number>
  cardHeightUniform: THREE.UniformNode<number>
  overallOpacityUniform: THREE.UniformNode<number>
  spacing = 16
  interactionManager: InteractionManager
  fbmMap: Texture

  hourglassIntensity = uniform(0.15)
  hourglassCenter = uniform(0.5)
  hourglassWidth = uniform(0.3)

  constructor({
    textScene,
    debugFolder,
    resources,
  }: {
    textScene: TextScene
    debugFolder?: FolderApi
    resources: {
      [key: string]: Texture
    }
  }) {
    this.experience = Experience.getInstance()
    this.renderer = this.experience.renderer.instance
    this.sizes = this.experience.sizes
    this.textScene = textScene
    this.debugFolder = debugFolder
    this.resources = resources

    this.interactionManager = textScene.interactionManager

    this.screenHeightUniform = uniform(this.sizes.height)
    this.cardHeightUniform = uniform(0)
    this.overallOpacityUniform = uniform(0)

    this.projectsElement = document.querySelector("[data-projects]")
    this.cardsElements = Array.from(this.projectsElement?.children || []).map(
      (child) => child.firstChild,
    ) as HTMLElement[]

    this.fbmMap = resources.fbm
    this.fbmMap.wrapS = THREE.RepeatWrapping
    this.fbmMap.wrapT = THREE.RepeatWrapping

    this.createProjectListScene()
    this.setupPostProcessing()

    this.createCardBounds()

    this.createProjectLeaf()
    this.createProjectImage()

    this.cardsElements.forEach((cardEl) => {
      this.createCard(cardEl)
    })

    this.setEventListeners()

    this.setDebug()
  }

  createProjectListScene() {
    // Recreate the scene because of the multi-render pass
    const { width, height } = this.sizes

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(
      this.distanceFromCamera,
      width / height,
    )
    this.camera.name = "works-project-list-camera"

    this.camera.position.set(0, 0, this.distanceFromCamera)
    this.camera.lookAt(0, 0, 0)

    this.syncDimensions()

    this.camera.updateProjectionMatrix()

    this.scene.add(this.camera)
  }

  createProjectImage() {
    this.projectImage = new Image({
      scene: this.textScene.scene,
      debugFolder: this.debugFolder,
      fbmMap: this.fbmMap,
      resources: this.resources,
    })
  }

  createProjectLeaf() {
    this.projectLeaf = new ProjectLeaf({
      scene: this.scene,
      textScene: this.textScene,
      interactionManager: this.interactionManager,
      camera: this.camera,
      // userData: { ...project, index },
    })
  }

  setupPostProcessing() {
    const scenePass = pass(this.scene, this.camera)
    this.postProcessing = new THREE.PostProcessing(this.renderer, scenePass)
    this.scenePassTextureNode = scenePass.getTextureNode()

    //
    // Distortion

    // Apply hourglass UV distortion to projectListOutput
    const uvs = uv()
    const v = uvs.y
    // Calculate distance from center normalized by width
    // This creates maximum distortion at top (v=0) and bottom (v=1), none at center
    const distFromCenter = abs(v.sub(this.hourglassCenter))
    const normalizedDist = distFromCenter.div(this.hourglassWidth)
    // Create hourglass intensity: 0 at center, 1 at edges (clamped)
    let hourglassFactor = clamp(normalizedDist, 0, 1).mul(
      this.hourglassIntensity,
    )

    // Remove distortion in the middle region corresponding to cardBounds
    // Card is centered on screen, so center is at v = 0.5
    const screenCenter = float(0.5)
    const cardHeightUV = this.cardHeightUniform.div(this.screenHeightUniform)
    const cardHalfHeightUV = cardHeightUV.div(2)
    const cardTop = screenCenter.sub(cardHalfHeightUV)
    const cardBottom = screenCenter.add(cardHalfHeightUV)
    // Smooth transition width for blending between card region and distorted areas
    const transitionWidth = cardHeightUV.mul(1.5) // 10% of card height for smooth transition
    // Check if v is within the cardBounds region, but keep the transition OUTSIDE the card
    // Top fade: 0 -> 1 in [cardTop - w, cardTop] (fully 1 inside the card)
    // Bottom fade: 1 -> 0 in [cardBottom, cardBottom + w] (fully 1 inside the card)
    const isInCardRegion = smoothstep(
      cardTop.sub(transitionWidth),
      cardTop,
      v,
    ).mul(smoothstep(cardBottom.add(transitionWidth), cardBottom, v))
    // Set hourglassFactor to 0 when in card region
    hourglassFactor = hourglassFactor.mul(isInCardRegion.oneMinus())
    // Apply horizontal pinch: scale X by mix(1, 0.3, hourglassFactor) to create hourglass effect
    // This pinches the top and bottom while keeping the middle normal
    // Using mix instead of direct subtraction to avoid complete collapse (scale = 0)
    const minScale = 0.3 // Minimum scale at edges (prevents complete collapse)
    const hourglassScaleX = mix(1, minScale, hourglassFactor)
    const hourglassScale = vec2(hourglassScaleX, 1)
    const hourglassUV = scaleUV(uvs, hourglassScale)
    // Sample the texture with distorted UVs
    // getTextureNode() returns a TextureNode that supports .sample()
    const distortedProjectListOutput = scenePass
      .getTextureNode()
      .sample(hourglassUV)

    //
    // Opacity (applied to entire output)
    //
    const opacityDistanceFromCenter = abs(v.sub(screenCenter))
    const opacityFlatAreaHalfHeight = cardHeightUV.div(2)

    const isInOpacityFlatArea = step(
      opacityDistanceFromCenter,
      opacityFlatAreaHalfHeight,
    )

    const fadeStart = opacityFlatAreaHalfHeight
    const fadeEnd = screenCenter
    const fadeRange = fadeEnd.sub(fadeStart)
    const normalizedFadeDistance = clamp(
      opacityDistanceFromCenter.sub(fadeStart).div(fadeRange),
      0,
      1,
    )

    const power = 4
    const fadeOpacity = pow(
      normalizedFadeDistance.oneMinus(),
      float(power),
    ).clamp(0, 1)

    const scrollOpacity = mix(fadeOpacity, float(1), isInOpacityFlatArea)
    const finalOpacity = scrollOpacity.mul(this.overallOpacityUniform)

    this.postProcessing.outputNode = mul(
      distortedProjectListOutput,
      finalOpacity,
    )
  }

  private syncDimensions() {
    const { height } = this.sizes

    this.camera.fov =
      2 * Math.atan(height / 2 / this.distanceFromCamera) * (180 / Math.PI)
  }

  setEventListeners() {
    this.experience.on(EVENTS.Works.BeforeEnter, () => {
      this.overallOpacityUniform.value = 0
    })

    this.experience.on(EVENTS.Works.Entered, () => {
      if (
        this.overallOpacityUniform.value === 1 ||
        window.location.pathname !== "/"
      ) {
        return
      }

      const tl = gsap.timeline()

      tl.fromTo(
        this.overallOpacityUniform,
        {
          value: 0,
          duration: 1,
          ease: "power2.out",
        },
        {
          value: 1,
        },
      )
    })

    this.experience.on(EVENTS.Works.BeforeEnter, () => {
      this.overallOpacityUniform.value = 0
    })

    this.experience.on(EVENTS.Works.LeaveWorks, () => {
      if (
        window.location.pathname !== "/" ||
        this.overallOpacityUniform.value < 1
      ) {
        return
      }
      gsap.to(this.overallOpacityUniform, {
        value: 0,
        duration: 1,
        ease: "power2.in",
      })
    })

    this.experience.on(EVENTS.Project.Exiting, () => {
      gsap.to(this.overallOpacityUniform, {
        value: 1,
        duration: 1,
        ease: "power2.out",
      })
    })
  }

  createCard(cardEl: HTMLElement) {
    if (!cardEl || !this.cardBounds) {
      console.warn("Card element or bounds not found")
      return
    }

    const group = new THREE.Group()

    const cardWidth = cardEl.clientWidth
    const cardHeight = cardEl.clientHeight
    const cardBounds = cardEl.getBoundingClientRect()

    const geometry = new THREE.PlaneGeometry(cardWidth, cardHeight)
    geometry.translate(cardWidth / 2, cardHeight / 2, 0)

    const material = new THREE.MeshBasicNodeMaterial()
    material.transparent = true
    material.opacity = this.cardMaterialSettings.opacity

    const colorNode = Fn(() => {
      const uvNode = uv()
      const cardColor = color(this.cardMaterialSettings.color)
      const borderColor = color(this.borderSettings.color)

      // Border on left and right sides
      // isBorder = 1 if in border region, 0 otherwise
      const leftBorder = step(uvNode.x, this.borderSettings.thickness)
      const rightBorder = step(
        float(1).sub(this.borderSettings.thickness),
        uvNode.x,
      )
      const isBorder = clamp(leftBorder.add(rightBorder), 0, 1)

      return mix(cardColor, borderColor, isBorder)
    })

    const opacityNode = Fn(() => {
      const uvNode = uv()

      // Horizontal scanlines effect (simulating VHS/CRT look)
      const scanlinePattern = fract(
        uvNode.y.mul(this.vhsSettings.scanlineCount),
      )
      const lines = step(0.5, scanlinePattern).mul(0.7).add(0.3)

      // Display frequency flicker (simulating CRT refresh rate)
      const timeNode = time
      const flicker = sin(timeNode.mul(60))
        .mul(this.vhsSettings.flickerIntensity)
        .add(1)

      return float(this.cardMaterialSettings.opacity).mul(lines).mul(flicker)
    })

    material.opacityNode = opacityNode()

    material.colorNode = colorNode()
    this.cardMaterials.push(material)

    const mesh = new THREE.Mesh(geometry, material)
    mesh.renderOrder = 0 // Card behind text so VHS lines don't affect text

    // Add click interaction to the mesh
    const cardIndex = this.cards.length
    this.interactionManager.add(mesh)
    mesh.addEventListener("click" as keyof THREE.Object3DEventMap, () => {
      const currentSection = this.experience.scrollInfo.currentSection
      if (currentSection?.name !== "works") {
        return
      }
      this.clickHandler(cardIndex)
    })

    // Add cursor pointer on hover
    mesh.addEventListener("mouseover" as keyof THREE.Object3DEventMap, () => {
      if (mesh.visible && this.renderer.domElement) {
        this.renderer.domElement.style.cursor = "pointer"
      }
    })

    mesh.addEventListener("mouseout" as keyof THREE.Object3DEventMap, () => {
      if (this.renderer.domElement) {
        this.renderer.domElement.style.cursor = "default"
      }
    })

    group.add(mesh)
    this.cards.push(group)

    // Query text elements for this card
    const textContentEl = cardEl.querySelector("[data-text-content]")
    if (textContentEl) {
      const textElements = Array.from(
        textContentEl.querySelectorAll("p"),
      ) as HTMLElement[]
      const cardTextMeshes: TextMeshData[] = []

      textElements.forEach((textEl) => {
        const textMesh = this.createTextMesh(textEl, group, cardBounds)
        if (textMesh) {
          cardTextMeshes.push(textMesh)
        }
      })

      this.textMeshes.push(cardTextMeshes)
    }

    this.scene.add(group)
  }

  createTextMesh(
    element: HTMLElement,
    cardGroup: THREE.Group,
    cardBounds: DOMRect,
  ): TextMeshData | null {
    const text = element.innerText
    if (!text) return null

    const computedStyle = window.getComputedStyle(element)
    const bounds = element.getBoundingClientRect()
    const y = bounds.top + this.experience.scrollInfo.actualScroll

    // Get styles similar to Text.getStyles()
    const {
      lineHeight,
      textAlign,
      letterSpacing,
      color: colorStyle,
    } = computedStyle

    let hexColor: string | undefined
    if (colorStyle.startsWith("oklch")) {
      hexColor = oklchToHex(colorStyle)
    } else if (colorStyle.startsWith("#")) {
      hexColor = colorStyle
    } else if (colorStyle.startsWith("rgb")) {
      hexColor = rgbToHex({
        r: parseFloat(colorStyle.split("(")[1].split(",")[0]),
        g: parseFloat(colorStyle.split("(")[1].split(",")[1]),
        b: parseFloat(colorStyle.split("(")[1].split(",")[2].split(")")[0]),
      })
    }

    // Get font and atlas from textScene
    const atlas = this.textScene.globalResources
      .atlasUbuntuSansMonoRegular as THREE.Texture
    const font = this.textScene.globalResources.bmfontUbuntuMonoRegular as {
      data: object
    }

    // Create geometry
    const geometry = new MSDFTextGeometry({
      text: text.toUpperCase(),
      font: font.data,
      lineHeight: parseFloat(lineHeight),
      letterSpacing: parseFloat(letterSpacing),
      width: bounds.width,
      height: bounds.height,
      align: textAlign,
      tabSize: 8,
    })

    // Create uniforms
    const visibleWorldsUniform = uniform(0)
    const hoverUniform = uniform(0)
    const progressUniform = uniform(1) // Already revealed
    const fadeUniform = uniform(text.length > 50)

    // Calculate word count for "already revealed" state
    const tokens = text.split(/(\s+|\n+)/)
    const wordCount = Math.ceil(tokens.length / 2)
    visibleWorldsUniform.value = wordCount // All words visible

    // Create material
    const material = new MSDFTextNodeMaterial({
      isSmooth: 1,
      strokeOutsetWidth: 0,
      strokeInsetWidth: 0,
      threshold: 0.5,
      map: atlas,
      fade: fadeUniform,
      progress: progressUniform,
      color: color(hexColor || "#D9D9D9"),
      noiseTexture: this.experience.globalResources.perlin as THREE.Texture,
    })

    // Set up colorNode with hover and scramble support (like Text.createMesh())
    const originalColor = material.color
    const wordIndexAttr = attribute("wordIndex", "float")
    const isScrambled = step(visibleWorldsUniform, wordIndexAttr)
    const darkerColor = originalColor.mul(0.2)
    const hoverColorNode = originalColor.mul(1.5)
    const normalColor = mix(originalColor, darkerColor, isScrambled)
    material.colorNode = mix(normalColor, hoverColorNode, hoverUniform)

    material.side = THREE.DoubleSide

    // Create mesh
    const mesh = new THREE.Mesh(geometry, material)
    mesh.rotateX(Math.PI)
    mesh.renderOrder = 1 // On top of card so text is not affected by VHS lines

    // Calculate position relative to card group's local origin
    // The card mesh is positioned at (cardWidth/2, cardHeight/2, 0) in local space
    // Text should be positioned relative to the card's top-left corner (0, 0) in local space
    // Convert text bounds to local space relative to card bounds
    const textLocalX = bounds.left - cardBounds.left
    const textLocalY = cardBounds.top + cardBounds.height - bounds.top

    // Adjust for card mesh offset (card mesh is at width/2, height/2)
    const cardMeshOffsetY = cardBounds.height / 2

    // Position text relative to card group origin (z > 0 = in front of card so VHS lines don't show on text)
    // Text origin is at bottom-left, so we need to account for that
    mesh.position.set(
      textLocalX,
      textLocalY - cardMeshOffsetY + bounds.height / 2 + bounds.height,
      0.01,
    )

    // Add to card group
    cardGroup.add(mesh)

    return {
      mesh,
      material,
      geometry,
      element,
      bounds,
      y,
    }
  }

  createCardBounds() {
    const firstText = this.cardsElements[0]
    const firstTextBounds = firstText.getBoundingClientRect()

    if (!firstText || !firstTextBounds || !this.projectsElement) {
      console.warn("First text or projects element not found")
      return
    }

    this.containerBounds = this.projectsElement.getBoundingClientRect()
    this.cardBounds = firstTextBounds
    this.cardY = this.cardBounds.top + this.experience.scrollInfo.actualScroll

    // Update card height uniform
    if (this.cardBounds) {
      this.cardHeightUniform.value = this.cardBounds.height
    }
  }

  clickHandler(cardIndex: number) {
    const cardElement = this.cardsElements[cardIndex]
    if (!cardElement) {
      console.warn(`Card element at index ${cardIndex} not found`)
      return
    }

    const { lenis, sizes, navigate } = this.experience
    const { offsetTop, clientHeight } = cardElement

    const y = offsetTop - sizes.height / 2 + clientHeight / 2

    lenis?.scrollTo(y, {
      duration: 0.3,
      onComplete: () => {
        // lenis stop
        lenis.stop()
        navigate(projects[cardIndex].url)
      },
    })

    gsap.to(this.overallOpacityUniform, {
      value: 0,
      duration: 0.3,
      delay: 0.1,
      ease: "power2.inOut",
    })
  }

  updateFirstCardPosition(card: THREE.Group) {
    if (!this.cardBounds || !this.containerBounds || !this.projectsElement) {
      console.warn("Card or container bounds not found")
      return
    }

    const currentScroll = this.experience.scrollInfo.animatedScroll
    const projectsLength = this.cardsElements.length

    const cardHeight = this.cardBounds.height

    const containerOffsetTop = this.projectsElement.offsetTop
    const margin = this.sizes.isMobile ? 0.4 : 0.125
    const containerDelta = this.sizes.height * margin
    const containerStart = containerOffsetTop - containerDelta

    const clampedContainerStart = Math.min(containerStart, currentScroll) // 2373 is the currentScroll when the first items is in place
    const containerHeightDelta =
      (this.containerBounds.height / projectsLength) * (projectsLength - 1)
    const containerOffsetBottom = containerStart + containerHeightDelta
    const cardsStart = cardHeight / -2
    const cardsEnd =
      cardsStart +
      (cardHeight * (projectsLength - 1) + this.spacing * (projectsLength - 1))
    const scaledY = scaleValue(
      currentScroll,
      [clampedContainerStart, containerOffsetBottom],
      [cardsStart, cardsEnd],
    )

    const cardsYScroll = containerStart + scaledY

    const positionY =
      -this.cardY + cardsYScroll + this.sizes.height / 2 - cardHeight / 2

    const positionX = this.cardBounds.left - this.sizes.width / 2

    card.position.y = positionY
    card.position.x = positionX
  }

  updateCardAtIndex(card: THREE.Group, index: number) {
    if (!this.cardBounds) {
      console.warn("Card bounds not found")
      return
    }

    const cardHeight = this.cardBounds.height

    const firstCardPositionY = this.cards[0].position.y

    const positionX = this.cards[0].position.x
    const positionY = firstCardPositionY - index * (cardHeight + this.spacing)

    card.position.y = positionY
    card.position.x = positionX
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const debugFolder = this.debugFolder.addFolder({
      title: "💅 Project List",
      expanded: false,
    })

    debugFolder.addBinding(this.hourglassIntensity, "value", {
      label: "Hourglass Intensity",
      min: -1,
      max: 1,
      step: 0.001,
    })

    debugFolder.addBinding(this.hourglassCenter, "value", {
      label: "Hourglass Center",
      min: 0,
      max: 1,
      step: 0.01,
    })

    debugFolder.addBinding(this.hourglassWidth, "value", {
      label: "Hourglass Width",
      min: -0.5,
      max: 0.5,
      step: 0.001,
    })

    const cardsFolder = debugFolder.addFolder({
      title: "Cards",
      expanded: false,
    })

    cardsFolder
      .addBinding(this.cardMaterialSettings, "opacity", {
        label: "Opacity",
        min: 0,
        max: 1,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.cardMaterials.forEach((material) => {
          material.opacity = ev.value
        })
      })

    cardsFolder
      .addBinding(this.cardMaterialSettings, "color", {
        label: "Color",
        view: "color",
      })
      .on("change", () => {
        // Re-create the colorNode with the updated card color
        this.cardMaterials.forEach((material) => {
          const colorNode = Fn(() => {
            const uvNode = uv()
            const cardColor = color(this.cardMaterialSettings.color)
            const borderColor = color(this.borderSettings.color)

            const leftBorder = step(uvNode.x, this.borderSettings.thickness)
            const rightBorder = step(
              float(1).sub(this.borderSettings.thickness),
              uvNode.x,
            )
            const isBorder = clamp(leftBorder.add(rightBorder), 0, 1)

            return mix(cardColor, borderColor, isBorder)
          })

          material.colorNode = colorNode()
          material.needsUpdate = true
        })
      })

    // VHS effect debug controls
    const vhsFolder = cardsFolder.addFolder({
      title: "📺 VHS Effect",
      expanded: false,
    })

    vhsFolder.addBinding(this.vhsSettings.scanlineCount, "value", {
      label: "Scanline Count",
      min: 10,
      max: 300,
      step: 1,
    })

    vhsFolder.addBinding(this.vhsSettings.flickerIntensity, "value", {
      label: "Flicker Intensity",
      min: 0,
      max: 0.2,
      step: 0.001,
    })

    // Border settings debug controls
    const borderFolder = cardsFolder.addFolder({
      title: "Border",
      expanded: false,
    })

    borderFolder.addBinding(this.borderSettings.thickness, "value", {
      label: "Thickness",
      min: 0,
      max: 0.1,
      step: 0.001,
    })

    borderFolder
      .addBinding(this.borderSettings, "color", {
        label: "Color",
        view: "color",
      })
      .on("change", (ev) => {
        const newBorderColor = color(ev.value)

        this.cardMaterials.forEach((material) => {
          // Re-create the colorNode with the new border color
          const colorNode = Fn(() => {
            const uvNode = uv()
            const cardColor = color(this.cardMaterialSettings.color)
            const borderColor = newBorderColor

            const leftBorder = step(uvNode.x, this.borderSettings.thickness)
            const rightBorder = step(
              float(1).sub(this.borderSettings.thickness),
              uvNode.x,
            )
            const isBorder = clamp(leftBorder.add(rightBorder), 0, 1)

            return mix(cardColor, borderColor, isBorder)
          })

          material.colorNode = colorNode()
          material.needsUpdate = true
        })
      })
  }

  update() {
    this.interactionManager.update()

    this.cards.forEach((card, index) => {
      if (index === 0) {
        this.updateFirstCardPosition(card)
      } else {
        this.updateCardAtIndex(card, index)
      }
    })

    this.projectImage?.update()

    this.postProcessing.render()
  }

  resize() {
    // Keep camera in sync with viewport so cards/texts align correctly
    this.camera.aspect = this.sizes.width / this.sizes.height
    this.syncDimensions()
    this.camera.updateProjectionMatrix()

    this.createCardBounds()

    // Update screen height uniform
    this.screenHeightUniform.value = this.sizes.height

    // Card height uniform is updated in createCardBounds()

    // Update mesh geometries to match new card dimensions
    this.cards.forEach((card, index) => {
      const cardEl = this.cardsElements[index]
      if (!cardEl) return

      const mesh = card.children[0] as THREE.Mesh
      if (!mesh || !(mesh.geometry instanceof THREE.PlaneGeometry)) return

      const cardWidth = cardEl.clientWidth
      const cardHeight = cardEl.clientHeight

      // Dispose old geometry
      mesh.geometry.dispose()

      // Create new geometry with updated dimensions
      const geometry = new THREE.PlaneGeometry(cardWidth, cardHeight)
      geometry.translate(cardWidth / 2, cardHeight / 2, 0)

      mesh.geometry = geometry
    })

    // Update text meshes: bounds, geometry, and position (same formula as createTextMesh)
    this.textMeshes.forEach((cardTextMeshes, cardIndex) => {
      const cardEl = this.cardsElements[cardIndex]
      const cardBounds = cardEl?.getBoundingClientRect()
      if (!cardBounds) return

      const cardMeshOffsetY = cardBounds.height / 2

      cardTextMeshes.forEach((textData) => {
        const newBounds = textData.element.getBoundingClientRect()
        textData.bounds = newBounds
        textData.y = newBounds.top + this.experience.scrollInfo.actualScroll

        textData.geometry.update({
          width: newBounds.width,
          height: newBounds.height,
        })

        // Recompute local position relative to card (matches createTextMesh)
        const textLocalX = newBounds.left - cardBounds.left
        const textLocalY = cardBounds.top + cardBounds.height - newBounds.top
        textData.mesh.position.set(
          textLocalX,
          textLocalY -
            cardMeshOffsetY +
            newBounds.height / 2 +
            newBounds.height,
          0.01,
        )
      })
    })

    this.projectImage?.resize()
  }
}
